/**
 * InAppUpsells Component
 * Displays contextual upsell banners for sample packs, plugins, and subscriptions
 */

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  X,
  Sparkles,
  Music,
  Plug,
  Crown,
  Package,
  ArrowRight,
  Headphones,
  Layers,
  Zap,
} from 'lucide-react';
import { Button } from '../../ui/button';

// Upsell types and their configurations
const UPSELL_CONFIG = {
  sample_packs: {
    icon: Music,
    gradient: 'from-emerald-500/20 to-teal-500/20',
    borderColor: 'border-emerald-500/30',
    accentColor: 'text-emerald-400',
    title: 'Expand Your Sound Library',
    description: 'Get 500+ premium samples across drums, bass, synths, and more.',
    cta: 'Browse Sample Packs',
    link: '/marketplace?tab=samples',
    badge: 'NEW',
  },
  plugins: {
    icon: Plug,
    gradient: 'from-violet-500/20 to-indigo-500/20',
    borderColor: 'border-violet-500/30',
    accentColor: 'text-violet-400',
    title: 'Level Up Your Mix',
    description: 'Professional plugins for mastering, EQ, compression, and more.',
    cta: 'Explore Plugins',
    link: '/marketplace?tab=plugins',
    badge: 'PRO',
  },
  presets: {
    icon: Layers,
    gradient: 'from-amber-500/20 to-orange-500/20',
    borderColor: 'border-amber-500/30',
    accentColor: 'text-amber-400',
    title: 'Pro Guitar Tones',
    description: 'Download community presets from professional guitarists.',
    cta: 'Browse Presets',
    link: '/marketplace?tab=presets',
    badge: 'HOT',
  },
  subscription: {
    icon: Crown,
    gradient: 'from-amber-500/20 to-yellow-500/20',
    borderColor: 'border-amber-500/30',
    accentColor: 'text-amber-400',
    title: 'Unlock All Features',
    description: 'Get unlimited tracks, premium plugins, and sell your own presets.',
    cta: 'View Plans',
    link: '/pricing',
    badge: 'SAVE 15%',
  },
};

// Featured products for upsells
const FEATURED_PRODUCTS = [
  {
    id: 'pack-drums-essential',
    name: 'Essential Drums Pack',
    type: 'sample_pack',
    price: 9.99,
    originalPrice: 14.99,
    image: '/images/sample-drums.jpg',
    samples: 120,
  },
  {
    id: 'pack-synth-waves',
    name: 'Synth Waves Collection',
    type: 'sample_pack',
    price: 12.99,
    originalPrice: 19.99,
    image: '/images/sample-synth.jpg',
    samples: 85,
  },
  {
    id: 'plugin-master-suite',
    name: 'Mastering Suite Pro',
    type: 'plugin',
    price: 19.99,
    originalPrice: 29.99,
    image: '/images/plugin-master.jpg',
    rating: 4.9,
  },
];

// Inline banner upsell
const UpsellBanner = ({ type, onDismiss, compact = false }) => {
  const config = UPSELL_CONFIG[type];
  if (!config) return null;

  const Icon = config.icon;

  if (compact) {
    return (
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        className={`flex items-center justify-between p-3 rounded-lg bg-gradient-to-r ${config.gradient} border ${config.borderColor}`}
      >
        <div className="flex items-center gap-3">
          <Icon className={`w-5 h-5 ${config.accentColor}`} />
          <span className="text-sm text-zinc-300">{config.title}</span>
        </div>
        <div className="flex items-center gap-2">
          <Link to={config.link}>
            <Button size="sm" variant="ghost" className={`${config.accentColor} hover:bg-white/10`}>
              {config.cta}
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
          <button
            onClick={onDismiss}
            className="p-1 text-zinc-500 hover:text-zinc-300 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className={`relative p-6 rounded-xl bg-gradient-to-br ${config.gradient} border ${config.borderColor} overflow-hidden`}
    >
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full blur-3xl" />
      
      {/* Badge */}
      {config.badge && (
        <span className={`absolute top-3 right-3 px-2 py-0.5 ${config.accentColor} bg-black/30 text-xs font-semibold rounded-full`}>
          {config.badge}
        </span>
      )}

      <div className="relative flex items-start gap-4">
        <div className={`p-3 rounded-xl bg-black/20 ${config.accentColor}`}>
          <Icon className="w-8 h-8" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-zinc-100 mb-1">{config.title}</h3>
          <p className="text-sm text-zinc-400 mb-4">{config.description}</p>
          <Link to={config.link}>
            <Button className={`bg-white/10 hover:bg-white/20 ${config.accentColor}`}>
              {config.cta}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>

      {/* Dismiss button */}
      <button
        onClick={onDismiss}
        className="absolute top-3 left-3 p-1 text-zinc-500 hover:text-zinc-300 transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
    </motion.div>
  );
};

// Featured product card
const FeaturedProductCard = ({ product, onPurchase }) => {
  const discount = Math.round((1 - product.price / product.originalPrice) * 100);

  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className="bg-zinc-800/50 rounded-xl border border-zinc-700 overflow-hidden cursor-pointer group"
      onClick={() => onPurchase?.(product)}
    >
      <div className="h-24 bg-gradient-to-br from-zinc-700 to-zinc-800 flex items-center justify-center relative">
        {product.type === 'sample_pack' ? (
          <Headphones className="w-10 h-10 text-zinc-500" />
        ) : (
          <Plug className="w-10 h-10 text-zinc-500" />
        )}
        {discount > 0 && (
          <span className="absolute top-2 right-2 px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded">
            -{discount}%
          </span>
        )}
      </div>
      <div className="p-3">
        <h4 className="font-medium text-zinc-200 text-sm mb-1 truncate">{product.name}</h4>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-emerald-400 font-bold">${product.price}</span>
            {product.originalPrice > product.price && (
              <span className="text-zinc-500 text-sm line-through">${product.originalPrice}</span>
            )}
          </div>
          {product.samples && (
            <span className="text-xs text-zinc-500">{product.samples} samples</span>
          )}
          {product.rating && (
            <span className="text-xs text-amber-400">★ {product.rating}</span>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// Contextual upsell panel (shows based on user activity)
const ContextualUpsellPanel = ({ context, onDismiss, userTier = 'free' }) => {
  const [dismissed, setDismissed] = useState(false);

  // Determine which upsell to show based on context
  const getUpsellType = () => {
    switch (context) {
      case 'recording':
        return userTier === 'free' ? 'subscription' : 'sample_packs';
      case 'mixing':
        return 'plugins';
      case 'guitar':
        return 'presets';
      case 'export':
        return userTier === 'free' ? 'subscription' : 'sample_packs';
      default:
        return 'sample_packs';
    }
  };

  if (dismissed) return null;

  const handleDismiss = () => {
    setDismissed(true);
    onDismiss?.();
  };

  return (
    <AnimatePresence>
      <UpsellBanner type={getUpsellType()} onDismiss={handleDismiss} />
    </AnimatePresence>
  );
};

// Main InAppUpsells component - shows in sidebar or modal
const InAppUpsells = ({ position = 'sidebar', currentContext, userTier = 'free' }) => {
  const [showUpsells, setShowUpsells] = useState(true);
  const [featuredIndex, setFeaturedIndex] = useState(0);

  // Rotate featured products every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setFeaturedIndex((prev) => (prev + 1) % FEATURED_PRODUCTS.length);
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  // Check if user dismissed upsells recently
  useEffect(() => {
    const dismissed = localStorage.getItem('upsells_dismissed');
    if (dismissed) {
      const dismissedTime = parseInt(dismissed, 10);
      // Don't show for 24 hours after dismissal
      if (Date.now() - dismissedTime < 24 * 60 * 60 * 1000) {
        setShowUpsells(false);
      }
    }
  }, []);

  const handleDismiss = () => {
    localStorage.setItem('upsells_dismissed', Date.now().toString());
    setShowUpsells(false);
  };

  if (!showUpsells) return null;

  if (position === 'inline') {
    return (
      <ContextualUpsellPanel
        context={currentContext}
        onDismiss={handleDismiss}
        userTier={userTier}
      />
    );
  }

  return (
    <div className="space-y-4">
      {/* Main upsell based on context */}
      <UpsellBanner
        type={userTier === 'free' ? 'subscription' : 'sample_packs'}
        onDismiss={handleDismiss}
      />

      {/* Featured product */}
      <div>
        <h4 className="text-sm font-medium text-zinc-400 mb-2 flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-amber-400" />
          Featured
        </h4>
        <FeaturedProductCard
          product={FEATURED_PRODUCTS[featuredIndex]}
        />
      </div>

      {/* Quick links */}
      <div className="grid grid-cols-2 gap-2">
        <Link
          to="/marketplace?tab=samples"
          className="p-3 bg-zinc-800/50 rounded-lg border border-zinc-700 hover:border-emerald-500/50 transition-colors text-center"
        >
          <Music className="w-5 h-5 mx-auto mb-1 text-emerald-400" />
          <span className="text-xs text-zinc-400">Samples</span>
        </Link>
        <Link
          to="/marketplace?tab=plugins"
          className="p-3 bg-zinc-800/50 rounded-lg border border-zinc-700 hover:border-violet-500/50 transition-colors text-center"
        >
          <Plug className="w-5 h-5 mx-auto mb-1 text-violet-400" />
          <span className="text-xs text-zinc-400">Plugins</span>
        </Link>
      </div>
    </div>
  );
};

export default InAppUpsells;
export { UpsellBanner, FeaturedProductCard, ContextualUpsellPanel, UPSELL_CONFIG };
